import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, FormControl, Grid, IconButton, InputLabel, Select, TextField,
    Tooltip, Typography } from "@mui/material";
import { AddCircle as IconAddCircle, Delete as IconDelete, Search as IconSearch } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";

import diagnosticoService from "../../services/diagnosticoService";
import DataGridBase from "../../components/common/DataGridBase";
import DialogYesNo from "../../components/DialogYesNo";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Navegabilidad de los diagnosticos. El mantenimiento esta en su propia pagina
// (/diagnostico/edit/:idAtencion/:numero), donde ademas se registra o se busca el
// comprobante con el que se factura.

const Index = () => {
    const { isReadOnly } = useMantenimiento("diagnostico");
    const navigate = useNavigate();

    const [filtros, setFiltros] = useState({ persona: "", fechaInicio: "", fechaFin: "" });
    const [buscando, setBuscando] = useState(false);

    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);

    const [openDialogEliminar, setOpenDialogEliminar] = useState(false);
    const [seleccionado, setSeleccionado] = useState(null);

    const fetchResults = useCallback(async () => {
        setCargando(true);

        const data = await diagnosticoService.getsDiagnostico(filtros)
            .catch((err) => { console.log("Err: ", err); return []; });

        // El grid pide un id unico por fila: el diagnostico se identifica por la
        // atencion y el numero.
        setResults((data || []).map((row) => ({ ...row, id: row.idAtencion + "-" + row.numero })));
        setCargando(false);
    }, [filtros])

    const buscar = () => {
        setBuscando(true);

        fetchResults().then(() => setBuscando(false));
    }

    useEffect(() => {
        setCargando(true);

        diagnosticoService.getsDiagnostico()
            .then((data) => {
                setResults((data || []).map((row) => ({ ...row, id: row.idAtencion + "-" + row.numero })));
                setCargando(false);
            })
            .catch(() => { setResults([]); setCargando(false); });
    }, [])

    const columns = [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'numero', headerName: 'N°', width: 70, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Link to={"/diagnostico/edit/" + cellValues.row.idAtencion + "/" + cellValues.row.numero}
                    style={{ color: 'inherit' }}>
                    {String(cellValues.row.numero).padStart(3, "0")}
                </Link>
            ) },
        { field: 'persona', headerName: 'Persona', width: 180, sortable: false, headerAlign: 'left' },
        { field: 'programa', headerName: 'Programa', width: 180, sortable: false, headerAlign: 'left' },
        { field: 'fecha', headerName: 'Fecha', width: 110, type: 'date', sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => moment(value).format('DD/MM/YYYY') },
        { field: 'idAtencion', headerName: 'Atencion', width: 90, sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => String(value).padStart(4, "0") },
        { field: 'puntuacion', headerName: 'Puntuacion', width: 130, sortable: false, headerAlign: 'left' },
        { field: 'comprobante', headerName: 'Comprobante', width: 140, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'eliminar', headerName: '', width: 70, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Tooltip title={"Eliminar diagnostico"}>
                    <span>
                        <IconButton aria-label={"eliminar diagnostico"} size="small" disabled={isReadOnly}
                            onClick={() => { setSeleccionado(cellValues.row); setOpenDialogEliminar(true); }}>
                            <IconDelete fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )
        },
    ];

    const eliminar = async () => {
        if (!seleccionado) return;

        const eliminado = await diagnosticoService.deleteDiagnostico(seleccionado.idAtencion, seleccionado.numero)
            .catch((err) => { console.log("Err: ", err); return false; });

        setSeleccionado(null);

        if (!eliminado) {
            setOpenDialogEliminar(false);
            return;
        }

        fetchResults();
    }

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Diagnosticos
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1} padding={0}>
                            <Grid item xs={8} sm={3}>
                                <TextField name="persona" label="Persona" value={filtros.persona} fullWidth
                                    onChange={(event) => setFiltros({ ...filtros, persona: event.target.value })}
                                    onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscar(); } }}
                                    size="small" />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField name="fechaInicio" label="Fecha Inicio" type="date" value={filtros.fechaInicio} fullWidth
                                    onChange={(event) => setFiltros({ ...filtros, fechaInicio: event.target.value })}
                                    size="small" InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField name="fechaFin" label="Fecha Fin" type="date" value={filtros.fechaFin} fullWidth
                                    onChange={(event) => setFiltros({ ...filtros, fechaFin: event.target.value })}
                                    size="small" InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                                <Button variant={"outlined"} size={"medium"} fullWidth disabled={buscando} onClick={() => { buscar(); }}>
                                    <IconSearch />
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                toolbarButtons={[
                                    {
                                        text: "Nuevo",
                                        icon: <IconAddCircle />,
                                        disabled: isReadOnly,
                                        onClick: () => navigate("/diagnostico/edit/0/0"),
                                    }
                                ]}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <DialogYesNo
                open={openDialogEliminar}
                titulo={"Eliminar Diagnostico"}
                texto={"Se eliminara el diagnostico de la atencion. Desea continuar?"}
                setYes={eliminar}
                setNo={() => { setSeleccionado(null); }}
                openClose={() => {
                    setOpenDialogEliminar(!openDialogEliminar);

                    if (openDialogEliminar) setSeleccionado(null);
                }}
            />
        </div>
    );
}

export default Index;